/**
 * Tool Display Configuration
 *
 * Maps technical tool names to human-readable labels and formatters
 * for the tool call visibility UI.
 */

import { ToolCallCategory } from './type';

interface ToolDisplayConfigEntry {
  label: string;
  activeLabel: string;
  icon: string;
  category: ToolCallCategory;
  resultFormatter: (data: unknown) => string;
}

export const TOOL_DISPLAY_CONFIG: Record<string, ToolDisplayConfigEntry> = {
  // Risk Assessment Tools
  find_risk_summary: {
    label: 'Risk Summary',
    activeLabel: 'Analyzing risk summary',
    icon: '\u26A0\uFE0F',
    category: 'risk',
    resultFormatter: (data) => {
      const d = data as { totals?: { at_risk_count?: number; total_skus?: number } };
      return `${d.totals?.at_risk_count ?? 0} at-risk of ${d.totals?.total_skus ?? 0} SKUs`;
    },
  },

  find_at_risk_skus: {
    label: 'At-Risk SKUs',
    activeLabel: 'Finding at-risk SKUs',
    icon: '\uD83D\uDD0D',
    category: 'risk',
    resultFormatter: (data) => {
      const d = data as {
        summary?: {
          at_risk_count?: number;
          severity_breakdown?: {
            critical?: number;
            warning?: number;
            info?: number;
          };
        };
        at_risk_skus?: unknown[];
      };
      const breakdown = d.summary?.severity_breakdown;
      if (breakdown) {
        const parts = [];
        if (breakdown.critical) parts.push(`${breakdown.critical} critical`);
        if (breakdown.warning) parts.push(`${breakdown.warning} warning`);
        if (breakdown.info) parts.push(`${breakdown.info} info`);
        if (parts.length > 0) return parts.join(', ');
      }
      return `${d.summary?.at_risk_count ?? d.at_risk_skus?.length ?? 0} SKUs identified`;
    },
  },

  find_stockout_date: {
    label: 'Stockout Date',
    activeLabel: 'Finding stockout date',
    icon: '\uD83D\uDCC5',
    category: 'risk',
    resultFormatter: (data) => {
      const d = data as { has_stockout?: boolean; stockout_date?: string; days_until_stockout?: number };
      if (!d.has_stockout) return 'No stockout projected';
      return `Stockout in ${d.days_until_stockout ?? '?'} days`;
    },
  },

  // Supply Analysis Tools
  get_supply_walk_for_sku: {
    label: 'Supply Walk',
    activeLabel: 'Loading supply walk',
    icon: '\uD83D\uDCCA',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as { summary?: { total_periods?: number }; data?: unknown[] };
      return `${d.summary?.total_periods ?? d.data?.length ?? 0} periods loaded`;
    },
  },

  find_low_point: {
    label: 'Low Point',
    activeLabel: 'Finding inventory low point',
    icon: '\uD83D\uDCC9',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as { low_point?: { ending_inventory?: number; date?: string } };
      return `${d.low_point?.ending_inventory ?? 0} units on ${d.low_point?.date ?? 'N/A'}`;
    },
  },

  // History Tools
  get_po_history_for_sku: {
    label: 'PO History',
    activeLabel: 'Getting PO history',
    icon: '\uD83D\uDCCB',
    category: 'history',
    resultFormatter: (data) => {
      const d = data as { summary?: { total_pos?: number }; purchase_orders?: unknown[] };
      const count = d.summary?.total_pos ?? d.purchase_orders?.length ?? 0;
      return `${count} purchase order${count !== 1 ? 's' : ''}`;
    },
  },

  // Comparison Tools
  compare_supply_walks: {
    label: 'Supply Walk Comparison',
    activeLabel: 'Comparing supply walks',
    icon: '\uD83D\uDD04',
    category: 'comparison',
    resultFormatter: (data) => {
      const d = data as { root_cause_indicator?: { primary_variable?: string; confidence?: string } };
      const cause = d.root_cause_indicator?.primary_variable ?? 'UNKNOWN';
      const conf = d.root_cause_indicator?.confidence ?? 'low';
      return `Primary: ${cause} (${conf} confidence)`;
    },
  },

  diagnose_shortfall: {
    label: 'Shortfall Diagnosis',
    activeLabel: 'Diagnosing shortfall',
    icon: '\uD83D\uDD2C',
    category: 'comparison',
    resultFormatter: (data) => {
      const d = data as {
        diagnosis?: { risk_type?: string; days_until_risk?: number };
        root_cause?: { primary_variable?: string; confidence?: string };
      };
      const risk = d.diagnosis?.risk_type?.toUpperCase() ?? 'UNKNOWN';
      const days = d.diagnosis?.days_until_risk;
      const cause = d.root_cause?.primary_variable ?? 'UNKNOWN';
      return `${risk} in ${days ?? '?'} days \u00B7 ${cause}`;
    },
  },

  // Transform Tools
  apply_transform: {
    label: 'Data Transform',
    activeLabel: 'Applying transform',
    icon: '\u26A1',
    category: 'transform',
    resultFormatter: (data) => {
      const d = data as { row_count?: number };
      return `${d.row_count ?? 0} rows`;
    },
  },

  // List Tools
  list_runs: {
    label: 'Run List',
    activeLabel: 'Listing runs',
    icon: '\uD83D\uDCD1',
    category: 'history',
    resultFormatter: (data) => {
      const d = data as { runs?: unknown[] };
      return `${d.runs?.length ?? 0} runs found`;
    },
  },
};

// Category colors for visual grouping
export const CATEGORY_COLORS: Record<ToolCallCategory, { bg: string; border: string; icon: string }> = {
  risk: { bg: '#fef3c7', border: '#f59e0b', icon: '#d97706' },      // Amber
  supply: { bg: '#dbeafe', border: '#3b82f6', icon: '#2563eb' },    // Blue
  comparison: { bg: '#f3e8ff', border: '#a855f7', icon: '#9333ea' }, // Purple
  history: { bg: '#fce7f3', border: '#ec4899', icon: '#db2777' },   // Pink
  transform: { bg: '#d1fae5', border: '#10b981', icon: '#059669' }, // Green
};
